/**
 * 회원 상세/수정 페이지
 *
 * 회원 데이터는 id 파라미터로 직접 API 조회한다 (목록 state 전달 방식이 아님).
 * 따라서 URL 직접 진입도 정상 동작한다.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { userService } from "../../services/userService";
import { useState } from "react";
import { BaeringUser } from "../../types/user";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ConfirmModal from "../../components/ui/ConfirmModal";
import { InputField, SelectField, TextareaField } from "../../components/ui/FormField";
import DetailActionButtons from "../../components/ui/DetailActionButtons";
import BackButton from "../../components/ui/BackButton";
import EmptyDetailState from "../../components/ui/EmptyDetailState";
import { formatDateTimeFull, formatDate } from "../../utils/format";

type BlacklistModal = { field: "device" | "phone" } | null;

export default function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState<Partial<BaeringUser>>({});
  const [blacklistModal, setBlacklistModal] = useState<BlacklistModal>(null);

  const { data: user, isLoading } = useQuery({
    queryKey: ["user", id],
    queryFn: () => userService.getUserById(Number(id)),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BaeringUser>) =>
      userService.updateUser(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => userService.deleteUser(Number(id)),
    onSuccess: () => navigate("/admin/member"),
  });

  const blacklistMutation = useMutation({
    mutationFn: ({ isBlacklist, isBlacklistPhone }: { isBlacklist: boolean; isBlacklistPhone: boolean }) =>
      userService.updateBlacklist(Number(id), isBlacklist, isBlacklistPhone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setBlacklistModal(null);
    },
  });

  const handleBlacklistConfirm = () => {
    if (!user || !blacklistModal) return;
    blacklistMutation.mutate({
      isBlacklist: blacklistModal.field === "device" ? !user.isBlacklist : user.isBlacklist,
      isBlacklistPhone: blacklistModal.field === "phone" ? !user.isBlacklistPhone : user.isBlacklistPhone,
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setFormData(user || {});
  };

  if (isLoading) return <LoadingSpinner />;

  if (!user) {
    return (
      <EmptyDetailState
        icon="fas fa-user-slash"
        message="유저를 찾을 수 없습니다"
        backTo="/admin/member"
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">회원 상세 정보</h1>
          <p className="text-gray-500 mt-2">회원 #{user.id}의 상세 정보</p>
        </div>
        <BackButton to="/admin/member" />
      </div>

      {/* User Avatar Card */}
      <div className="bg-primary rounded-xl shadow-lg p-8 mb-6 text-white">
        <div className="flex items-center space-x-6">
          <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center text-4xl font-bold backdrop-blur-sm">
            {user.userId.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-2">{user.userId}</h2>
            <div className="flex items-center space-x-4 text-white/80">
              <span>
                <i className="fas fa-phone mr-2"></i>
                {user.phoneNumber}
              </span>
              <span>
                <i className="fas fa-calendar-alt mr-2"></i>
                {formatDate(user.createdDt)}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {user.isAddFunction && (
              <div className="px-4 py-2 bg-marker-orange text-white rounded-lg font-semibold flex items-center space-x-2">
                <i className="fas fa-star"></i>
                <span>프리미엄</span>
              </div>
            )}
            {/* 기기 블랙리스트 빠른 토글 */}
            <button
              onClick={() => setBlacklistModal({ field: "device" })}
              title="기기 블랙리스트 전환"
              className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-all hover:opacity-80 ${
                user.isBlacklist
                  ? "bg-text-red text-white"
                  : "bg-white/20 text-white border border-white/40 hover:bg-white/30"
              }`}
            >
              <i className="fas fa-ban"></i>
              <span>기기 블랙리스트 {user.isBlacklist ? "해제" : "등록"}</span>
            </button>
            {/* 번호 블랙리스트 빠른 토글 */}
            <button
              onClick={() => setBlacklistModal({ field: "phone" })}
              title="번호 블랙리스트 전환"
              className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-all hover:opacity-80 ${
                user.isBlacklistPhone
                  ? "bg-text-red text-white"
                  : "bg-white/20 text-white border border-white/40 hover:bg-white/30"
              }`}
            >
              <i className="fas fa-ban"></i>
              <span>번호 블랙리스트 {user.isBlacklistPhone ? "해제" : "등록"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label={<><i className="fas fa-hashtag text-primary mr-2"></i>번호</>}
            value={user.id}
            disabled
          />

          <InputField
            label={<><i className="fas fa-user text-primary mr-2"></i>안드로이드 아이디</>}
            value={isEditing ? formData.userId || "" : user.userId}
            onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
            disabled={!isEditing}
          />

          <InputField
            label={<><i className="fas fa-mobile-alt text-primary mr-2"></i>기기 주소값</>}
            value={isEditing ? formData.deviceAddress || "" : user.deviceAddress}
            onChange={(e) => setFormData({ ...formData, deviceAddress: e.target.value })}
            disabled={!isEditing}
            inputClassName="font-mono text-sm"
          />

          <InputField
            label={<><i className="fas fa-phone text-primary mr-2"></i>연락처</>}
            value={isEditing ? formData.phoneNumber || "" : user.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            disabled={!isEditing}
          />

          {/* 디바이스 버전 — 편집 불가, 배지 표시 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-mobile-screen-button text-primary mr-2"></i>
              디바이스 버전
            </label>
            <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-bg-light flex items-center">
              <span
                className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                  user.deviceVersion === "V1"
                    ? "bg-blue-50 text-blue-600 border border-blue-200"
                    : user.deviceVersion === "V2"
                      ? "bg-purple-50 text-purple-600 border border-purple-200"
                      : "bg-gray-50 text-gray-600 border border-gray-200"
                }`}
              >
                <i className="fas fa-mobile-alt mr-1"></i>
                {user.deviceVersion}
              </span>
            </div>
          </div>

          <SelectField
            label={<><i className="fas fa-ban text-primary mr-2"></i>블랙리스트 (기기)</>}
            value={isEditing ? (formData.isBlacklist ? "true" : "false") : (user.isBlacklist ? "true" : "false")}
            onChange={(e) => setFormData({ ...formData, isBlacklist: e.target.value === "true" })}
            disabled={!isEditing}
          >
            <option value="false">일반</option>
            <option value="true">블랙</option>
          </SelectField>

          <SelectField
            label={<><i className="fas fa-mobile-alt text-primary mr-2"></i>블랙리스트 (휴대폰)</>}
            value={isEditing ? (formData.isBlacklistPhone ? "true" : "false") : (user.isBlacklistPhone ? "true" : "false")}
            onChange={(e) => setFormData({ ...formData, isBlacklistPhone: e.target.value === "true" })}
            disabled={!isEditing}
          >
            <option value="false">일반</option>
            <option value="true">블랙</option>
          </SelectField>

          <SelectField
            label={<><i className="fas fa-star text-primary mr-2"></i>추가기능 (프리미엄)</>}
            value={isEditing ? (formData.isAddFunction ? "true" : "false") : (user.isAddFunction ? "true" : "false")}
            onChange={(e) => setFormData({ ...formData, isAddFunction: e.target.value === "true" })}
            disabled={!isEditing}
          >
            <option value="false">비활성</option>
            <option value="true">활성</option>
          </SelectField>

          <InputField
            label={<><i className="fas fa-calendar-alt text-primary mr-2"></i>가입일</>}
            value={formatDateTimeFull(user.createdDt)}
            disabled
          />

          <TextareaField
            label={<><i className="fas fa-sticky-note text-primary mr-2"></i>메모</>}
            value={isEditing ? formData.memo || "" : user.memo || ""}
            onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
            disabled={!isEditing}
            rows={4}
            placeholder="회원에 대한 메모를 입력하세요..."
            className="md:col-span-2"
          />
        </div>

        <DetailActionButtons
          isNew={false}
          isEditing={isEditing}
          isSaving={updateMutation.isPending}
          deleteLabel="회원 삭제"
          onEdit={handleEdit}
          onSave={() => updateMutation.mutate(formData)}
          onCancelEdit={() => setIsEditing(false)}
          onDelete={() => setShowDeleteModal(true)}
        />
      </div>

      {showDeleteModal && (
        <ConfirmModal
          title="회원을 삭제하시겠습니까?"
          confirmLabel="삭제"
          isLoading={deleteMutation.isPending}
          onConfirm={() => { deleteMutation.mutate(); setShowDeleteModal(false); }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {/* 블랙리스트 토글 확인 모달 */}
      {blacklistModal && user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-80 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                (blacklistModal.field === "device" ? user.isBlacklist : user.isBlacklistPhone)
                  ? "bg-green-50"
                  : "bg-red-50"
              }`}>
                <i className={`fas ${
                  (blacklistModal.field === "device" ? user.isBlacklist : user.isBlacklistPhone)
                    ? "fa-circle-check text-green-500"
                    : "fa-ban text-text-red"
                }`}></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {blacklistModal.field === "device" ? "기기" : "번호"} 블랙리스트{" "}
                {(blacklistModal.field === "device" ? user.isBlacklist : user.isBlacklistPhone) ? "해제" : "등록"}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              {user.userId} 회원을{" "}
              {blacklistModal.field === "device" ? "기기" : "번호"} 블랙리스트에서{" "}
              {(blacklistModal.field === "device" ? user.isBlacklist : user.isBlacklistPhone) ? "해제" : "등록"}하시겠습니까?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setBlacklistModal(null)}
                disabled={blacklistMutation.isPending}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleBlacklistConfirm}
                disabled={blacklistMutation.isPending}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50 ${
                  (blacklistModal.field === "device" ? user.isBlacklist : user.isBlacklistPhone)
                    ? "bg-primary hover:bg-[#5FA060]"
                    : "bg-text-red hover:opacity-90"
                }`}
              >
                {blacklistMutation.isPending ? "처리 중..." : "확인"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
